import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import type { SchedulingCandidate } from "../../api/statelessClient";
import { getProjectRepository } from "../../data/projectRepository";
import { TimetableGrid } from "../timetable/TimetableDnd";
import type {
  TimetableSchedule,
  TimetableView,
  TimetableViewResponse
} from "../timetable/types";

const views: ReadonlyArray<{
  value: TimetableView;
  segment: "classes" | "teachers" | "rooms";
  label: string;
}> = [
  { value: "CLASS", segment: "classes", label: "班级" },
  { value: "TEACHER", segment: "teachers", label: "教师" },
  { value: "ROOM", segment: "rooms", label: "功能教室" }
];

export function CandidateTimetablePreview({
  projectId,
  candidate
}: {
  projectId: string;
  candidate: SchedulingCandidate;
}): React.JSX.Element {
  const [view, setView] = useState<TimetableView>("CLASS");
  const [weekParity, setWeekParity] = useState<"ODD" | "EVEN">("ODD");
  const [selectedObjectId, setSelectedObjectId] = useState<string | null>(null);
  const option = views.find((item) => item.value === view) ?? views[0];
  const timetable = useQuery({
    queryKey: [
      "relaxed-candidate-timetable",
      projectId,
      candidate.schedule_signature,
      view,
      weekParity
    ],
    queryFn: async () =>
      (await getProjectRepository()).candidateTimetable<TimetableViewResponse>(
        projectId,
        candidate,
        option.segment,
        null,
        weekParity
      ),
    staleTime: 5 * 60 * 1000
  });

  useEffect(() => {
    setSelectedObjectId(null);
  }, [view, weekParity]);

  if (timetable.isLoading) {
    return <p className="state-message compact">正在加载完整备选课表…</p>;
  }
  if (!timetable.data || timetable.isError) {
    return (
      <p className="error-message compact" role="alert">
        {timetable.error instanceof Error
          ? timetable.error.message
          : "备选课表暂时无法显示，请重试"}
      </p>
    );
  }

  const data = timetable.data;
  const activeObjectId =
    selectedObjectId ?? data.schedules[0]?.object.id ?? "";
  const shownSchedules: TimetableSchedule[] = activeObjectId
    ? data.schedules.filter((item) => item.object.id === activeObjectId)
    : [];
  return (
    <section className="candidate-preview" aria-label="完整备选课表">
      <div className="candidate-preview-controls">
        <div className="segmented-tabs" aria-label="课表视角">
          {views.map((item) => (
            <button
              className={view === item.value ? "active" : ""}
              type="button"
              key={item.value}
              aria-pressed={view === item.value}
              onClick={() => setView(item.value)}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="segmented-tabs" aria-label="单双周">
          {(["ODD", "EVEN"] as const).map((parity) => (
            <button
              className={weekParity === parity ? "active" : ""}
              type="button"
              key={parity}
              aria-pressed={weekParity === parity}
              onClick={() => setWeekParity(parity)}
            >
              {parity === "ODD" ? "单周" : "双周"}
            </button>
          ))}
        </div>
        <label className="field candidate-object-filter">
          <span>查看{option.label}</span>
          <select
            value={activeObjectId}
            onChange={(event) => setSelectedObjectId(event.target.value)}
          >
            {data.schedules.map((schedule) => (
              <option value={schedule.object.id} key={schedule.object.id}>
                {schedule.object.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      {shownSchedules.length === 0 ? (
        <p className="state-message compact">
          当前视角没有需要占用具体功能教室的课程。
        </p>
      ) : (
        shownSchedules.map((schedule) => (
          <TimetableGrid
            mode="READ_ONLY"
            key={`${view}:${weekParity}:${schedule.object.id}`}
            view={view}
            schedule={schedule}
            slots={data.slots}
            timetableRevision={data.timetable_revision}
          />
        ))
      )}
    </section>
  );
}
